import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, X, MessageCircle, ExternalLink } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { BrowserFrame, ChaleScreen, PizzariaScreen, DashboardScreen } from "./mockups/ProjectScreens";
import { PROJECTS as FALLBACK_PROJECTS } from "../data/projects";
import { api, imgSrc } from "../lib/api";
import { WA_PROJETOS, waProjeto } from "../lib/site";

// Preview ilustrativo usado apenas se um projeto estiver sem imagem.
const FALLBACK_SCREENS = {
  "chale-do-lago": <ChaleScreen />,
  pizzaria: <PizzariaScreen />,
  "sistema-gestao": <DashboardScreen />,
};

const Preview = ({ project }) =>
  project.imagem ? (
    <img
      src={imgSrc(project.imagem)}
      alt={`Preview do projeto ${project.nome} — ${project.categoria}`}
      className="h-full w-full rounded-xl border border-white/10 object-cover object-top"
      loading="lazy"
    />
  ) : (
    <BrowserFrame
      url={`demo.edalix.com.br/${project.slug}`}
      label={`Preview ilustrativo do projeto ${project.nome} — ${project.categoria}`}
    >
      {FALLBACK_SCREENS[project.slug] || <div className="h-full w-full bg-[#0A0A14]" />}
    </BrowserFrame>
  );

const ProjectModal = ({ project, onClose }) => {
  const hasUrl = project.url && project.url !== "#";

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    if (window.__lenis) window.__lenis.stop();
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      if (window.__lenis) window.__lenis.start();
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[70] flex items-center justify-center bg-[#030305]/90 p-4 backdrop-blur-xl sm:p-8"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Projeto ${project.nome}`}
      data-testid="project-modal"
    >
      <motion.div
        initial={{ opacity: 0, y: 40, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 30, scale: 0.97 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="gradient-border max-h-full w-full max-w-4xl overflow-y-auto rounded-3xl"
      >
        <div className="p-5 sm:p-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <span className="text-gradient text-[11px] font-semibold uppercase tracking-[0.28em]">
                {project.categoria}
              </span>
              <h3 className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl">
                {project.nome}
              </h3>
            </div>
            <button
              type="button"
              onClick={onClose}
              data-testid="project-modal-close-button"
              aria-label="Fechar preview do projeto"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white/70 transition-colors duration-300 hover:bg-white/10 hover:text-white"
            >
              <X style={{ width: 18, height: 18 }} />
            </button>
          </div>

          <div className="mt-6 h-64 overflow-hidden sm:h-[26rem]">
            <Preview project={project} />
          </div>

          <p className="mt-6 max-w-2xl text-sm font-light leading-relaxed text-white/60 md:text-base">
            {project.descricao}
          </p>

          <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
            {hasUrl ? (
              <a
                href={project.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="project-modal-open-button"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] px-7 py-3.5 text-sm font-semibold text-white transition-[transform,filter] duration-300 hover:scale-[1.03] hover:brightness-110"
              >
                Abrir projeto
                <ExternalLink className="h-4 w-4" />
              </a>
            ) : (
              <Link
                to={`/projetos/${project.slug}`}
                onClick={onClose}
                data-testid="project-modal-open-button"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] px-7 py-3.5 text-sm font-semibold text-white transition-[transform,filter] duration-300 hover:scale-[1.03] hover:brightness-110"
              >
                Ver detalhes
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            )}
            <a
              href={waProjeto(project.nome)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="project-modal-whatsapp-button"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-white/10"
            >
              <MessageCircle className="h-4 w-4" />
              Quero um projeto assim
            </a>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const SkeletonCard = () => (
  <div className="overflow-hidden rounded-3xl border border-white/10 bg-white/[0.02]">
    <div className="h-64 animate-pulse bg-white/[0.04] sm:h-72" />
    <div className="space-y-3 p-7">
      <div className="h-2.5 w-24 animate-pulse rounded-full bg-white/[0.07]" />
      <div className="h-5 w-40 animate-pulse rounded-full bg-white/[0.07]" />
      <div className="h-2.5 w-full animate-pulse rounded-full bg-white/[0.05]" />
      <div className="h-2.5 w-2/3 animate-pulse rounded-full bg-white/[0.05]" />
    </div>
  </div>
);

const Projects = () => {
  const [projects, setProjects] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api
      .get("/projects")
      .then(({ data }) => {
        const list = Array.isArray(data?.projects) ? data.projects : [];
        setProjects(list.length > 0 ? list : FALLBACK_PROJECTS.filter((p) => p.ativo !== false));
      })
      .catch(() => setProjects(FALLBACK_PROJECTS.filter((p) => p.ativo !== false)));
  }, []);

  return (
    <section id="projetos" data-testid="projects-section" className="relative py-24 lg:py-32">
      <div className="pointer-events-none absolute left-1/2 top-0 h-72 w-[42rem] -translate-x-1/2 rounded-full bg-[#5B8CFF]/8 blur-[140px]" />
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHeading
          eyebrow="PROJETOS EDALIX"
          title={
            <>
              PROJETOS EM <span className="text-gradient">DESTAQUE</span>
            </>
          }
          description="Conheça soluções digitais desenvolvidas pela EDALIX e já publicadas no ar."
        />

        <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {projects === null &&
            [0, 1, 2].map((i) => <SkeletonCard key={i} />)}
          {projects?.map((p, i) => {
            const hasUrl = p.url && p.url !== "#";
            return (
              <Reveal key={p.slug} delay={0.1 * i} className="h-full">
                <article
                  data-testid={`project-card-${p.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-3xl border border-white/10 bg-white/[0.02] transition-[transform,border-color] duration-500 hover:-translate-y-2 hover:border-[#00E5FF]/35"
                >
                  <button
                    type="button"
                    onClick={() => setSelected(p)}
                    data-testid={`project-preview-${p.slug}`}
                    aria-label={`Ampliar preview do projeto ${p.nome}`}
                    className="relative block h-64 w-full cursor-pointer overflow-hidden border-b border-white/10 bg-[#07070E] text-left sm:h-72"
                  >
                    <div
                      className={`h-full transition-transform duration-700 ease-out group-hover:scale-[1.04] group-hover:-translate-y-1 ${
                        p.imagem ? "" : "p-4"
                      }`}
                    >
                      <Preview project={p} />
                    </div>
                    <div className="pointer-events-none absolute inset-0 flex items-end justify-center bg-gradient-to-t from-[#030305]/85 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                      <span className="mb-5 inline-flex translate-y-2 items-center gap-1.5 rounded-full border border-white/20 bg-[#0B0B16]/85 px-4 py-2 text-xs font-semibold text-white backdrop-blur transition-transform duration-500 group-hover:translate-y-0">
                        Ver projeto
                        <ArrowUpRight className="h-3.5 w-3.5 text-[#00E5FF]" />
                      </span>
                    </div>
                  </button>

                  <div className="flex flex-1 flex-col p-7">
                    <span className="text-gradient text-[11px] font-semibold uppercase tracking-[0.28em]">
                      {p.categoria}
                    </span>
                    <h3 className="mt-3 font-display text-2xl font-bold tracking-tight">
                      {p.nome}
                    </h3>
                    <p className="mt-3 flex-1 text-sm font-light leading-relaxed text-white/60">
                      {p.descricao}
                    </p>
                    <div className="mt-6 flex items-center gap-3">
                      {hasUrl ? (
                        <a
                          href={p.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`project-cta-${p.slug}`}
                          className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] px-5 py-3 text-sm font-semibold text-white transition-[transform,filter] duration-300 hover:scale-[1.02] hover:brightness-110"
                        >
                          {p.botao || "VER PROJETO"}
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      ) : (
                        <Link
                          to={`/projetos/${p.slug}`}
                          data-testid={`project-cta-${p.slug}`}
                          className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] px-5 py-3 text-sm font-semibold text-white transition-[transform,filter] duration-300 hover:scale-[1.02] hover:brightness-110"
                        >
                          {p.botao || "VER PROJETO"}
                          <ArrowUpRight className="h-4 w-4" />
                        </Link>
                      )}
                      <a
                        href={waProjeto(p.nome)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`project-whatsapp-${p.slug}`}
                        aria-label={`Quero um projeto como ${p.nome} — falar no WhatsApp`}
                        className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white/70 transition-[border-color,color] duration-300 hover:border-[#00E5FF]/50 hover:text-[#00E5FF]"
                      >
                        <MessageCircle style={{ width: 17, height: 17 }} />
                      </a>
                    </div>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-14 flex justify-center">
            <a
              href={WA_PROJETOS}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="projects-cta-button"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-8 py-4 text-sm font-semibold text-white backdrop-blur transition-colors duration-300 hover:border-[#00E5FF]/50 hover:bg-white/10"
            >
              Ver projetos
              <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </Reveal>
      </div>

      <AnimatePresence>
        {selected && <ProjectModal project={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </section>
  );
};

export default Projects;
